// utils/axiosInstance.js
// Centralized axios instance with automatic token refresh interceptor
import axios from 'axios';
import AuthService from '../auth/AuthService.js';
import { notifySessionExpired } from './sessionExpiredNavigation.js';

// Create an instance of AuthService for token management
const authService = new AuthService();

// Token refresh tracker to prevent multiple simultaneous refresh attempts
let tokenRefreshPromise = null;

// Track 401 retry attempts per request
const retryCountMap = new Map();

// Create a centralized axios instance
const axiosInstance = axios.create();

// Request interceptor to validate and refresh tokens before every backend request
axiosInstance.interceptors.request.use(
  async (config) => {
    try {
      // Prevent multiple simultaneous token refresh calls
      if (!tokenRefreshPromise) {
        tokenRefreshPromise = authService.refreshIfNeeded();
      }
      await tokenRefreshPromise;
      tokenRefreshPromise = null;
      
      // Get the refreshed/valid access token
      const token = localStorage.getItem('access_token');
      if (token) {
        config.headers['access-token'] = token;
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      tokenRefreshPromise = null;
      // Remove all tokens on auth failure
      localStorage.removeItem('access_token');
      localStorage.removeItem('id_token');
      localStorage.removeItem('refresh_token');
      notifySessionExpired();
      // Re-throw error to prevent the request from proceeding with invalid tokens
      throw error;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle 401 errors (unauthorized)
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If we get a 401, try to refresh token and retry the request (max 3 times)
    if (error.response?.status === 401 && originalRequest) {
      // Create a unique key for this request to track retry count
      const requestKey = originalRequest.url + (originalRequest.method || 'GET');
      const retryCount = retryCountMap.get(requestKey) || 0;
      
      // Stop refreshing after 3 unauthorized attempts
      if (retryCount >= 3) {
        console.error('Max retry attempts (3) reached for 401 error. Stopping refresh.');
        retryCountMap.delete(requestKey);
        // Clear all tokens
        localStorage.removeItem('access_token');
        localStorage.removeItem('id_token');
        localStorage.removeItem('refresh_token');
        tokenRefreshPromise = null;
        notifySessionExpired();
        return Promise.reject(error);
      }
      
      // Increment retry count
      retryCountMap.set(requestKey, retryCount + 1);
      
      try {
        const refresh_token = localStorage.getItem('refresh_token');
        if (!refresh_token) {
          console.error('No refresh token available for 401 retry');
          localStorage.removeItem('access_token');
          localStorage.removeItem('id_token');
          localStorage.removeItem('refresh_token');
          retryCountMap.delete(requestKey);
          tokenRefreshPromise = null;
          notifySessionExpired();
          return Promise.reject(error);
        }

        const refreshed = await authService.refreshToken();
        if (authService.applyRefreshedTokens(refreshed)) {
          const token = localStorage.getItem('access_token');
          if (token && originalRequest) {
            originalRequest.headers['access-token'] = token;
            tokenRefreshPromise = null;
            return axiosInstance.request(originalRequest);
          }
        } else {
          throw new Error('Refresh failed - no tokens returned');
        }
      } catch (refreshError) {
        console.error(`Token refresh on 401 failed (attempt ${retryCount + 1}/3):`, refreshError);
        
        // If this was the last attempt, clear tokens and stop
        if (retryCount + 1 >= 3) {
          console.error('Max retry attempts reached. Clearing tokens and stopping.');
          localStorage.removeItem('access_token');
          localStorage.removeItem('id_token');
          localStorage.removeItem('refresh_token');
          retryCountMap.delete(requestKey);
          tokenRefreshPromise = null;
          notifySessionExpired();
          return Promise.reject(error);
        }
        
        // If not the last attempt, the retry count is already incremented
        // and will be checked on the next 401
        tokenRefreshPromise = null;
        return Promise.reject(error);
      }
    }
    if (error.response?.status === 401) {
      notifySessionExpired();
    }
    return Promise.reject(error);
  }
);

// Function to clear retry count map (useful for logout)
export const clearRetryCounts = () => {
  retryCountMap.clear();
};

export default axiosInstance;

